import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Container,
  TopInfoWrapper,
  ResourceBlock,
  ResourceTitle,
  ResourceValue,
  ResourceBar,
  ResourceBarFill,
  ResourceInfo,
  CpuInfoSection,
  CpuInfoGrid,
  CpuInfoItem,
  CpuInfoLabel,
  CpuInfoValue,
} from './styles'
import { InfoBlock } from '../../../../../components'

type OverviewProps = {
  hypervisor: any,
}

const Overview: FC<OverviewProps> = ({ hypervisor }) => {
  const [t] = useTranslation()
  const [cpuInfo, setCpuInfo] = useState<any>(() => {
    if (hypervisor?.cpu_info) {
      try {
        return JSON.parse(hypervisor.cpu_info)
      } catch (e) {
        return null
      }
    }
    return null
  })

  const generalInfo = [
    {
      title: t("ID"),
      id: '1',
      value: hypervisor?.id
    },
    {
      title: t("REGION"),
      id: '2',
      value: hypervisor?.region
    },
    {
      title: t("STATE"),
      id: '3',
      value: hypervisor?.state === 'up' ? t("UP") : t("DOWN")
    },
    {
      title: t("STATUS"),
      id: '4',
      value: hypervisor?.status === 'enabled' ? t("ENABLED") : t("DISABLED")
    },
    {
      title: t("HOST_IP"),
      id: '5',
      value: hypervisor?.host_ip
    },
    {
      title: t("HYPERVISOR_TYPE"),
      id: '6',
      value: hypervisor?.hypervisor_type
    },
    {
      title: t("HYPERVISOR_VERSION"),
      id: '7',
      value: hypervisor?.hypervisor_version
    },
    {
      title: t("RUNNING_VMS"),
      id: '8',
      value: hypervisor?.running_vms
    },
    {
      title: t("CURRENT_WORKLOAD"),
      id: '9',
      value: hypervisor?.current_workload
    },
  ]

  const calculatePercentage = (used: number, total: number) => {
    if (!total || total === 0) return 0
    return Math.round((used / total) * 100)
  }

  const vcpuPercentage = calculatePercentage(hypervisor?.vcpus_used, hypervisor?.vcpus)
  const memoryPercentage = calculatePercentage(hypervisor?.memory_mb_used, hypervisor?.memory_mb)
  const diskPercentage = calculatePercentage(hypervisor?.local_gb_used, hypervisor?.local_gb)

  return (
    <Container>
      <TopInfoWrapper>
        <InfoBlock
          data={generalInfo}
          title={t("GENERAL_INFORMATION")}
          customStyles={{ width: "545px", flex: 'auto 1', marginRight: '10px' }}
        />
      </TopInfoWrapper>

      <TopInfoWrapper style={{ marginTop: '20px' }}>
        <ResourceBlock>
          <ResourceTitle>{t("VCPUS")}</ResourceTitle>
          <ResourceValue>
            {hypervisor?.vcpus_used} / {hypervisor?.vcpus}
          </ResourceValue>
          <ResourceBar>
            <ResourceBarFill percentage={vcpuPercentage} />
          </ResourceBar>
          <ResourceInfo>{vcpuPercentage}% {t("USED")}</ResourceInfo>
        </ResourceBlock>

        <ResourceBlock>
          <ResourceTitle>{t("MEMORY")}</ResourceTitle>
          <ResourceValue>
            {hypervisor?.memory_mb_used} MB / {hypervisor?.memory_mb} MB
          </ResourceValue>
          <ResourceBar>
            <ResourceBarFill percentage={memoryPercentage} />
          </ResourceBar>
          <ResourceInfo>
            {memoryPercentage}% {t("USED")} | {hypervisor?.free_ram_mb} MB {t("FREE")}
          </ResourceInfo>
        </ResourceBlock>

        <ResourceBlock>
          <ResourceTitle>{t("DISK")}</ResourceTitle>
          <ResourceValue>
            {hypervisor?.local_gb_used} GB / {hypervisor?.local_gb} GB
          </ResourceValue>
          <ResourceBar>
            <ResourceBarFill percentage={diskPercentage} />
          </ResourceBar>
          <ResourceInfo>
            {diskPercentage}% {t("USED")} | {hypervisor?.free_disk_gb} GB {t("FREE")}
          </ResourceInfo>
        </ResourceBlock>
      </TopInfoWrapper>

      {cpuInfo && Object.keys(cpuInfo).length > 0 && (
        <CpuInfoSection>
          <ResourceTitle>{t("CPU_INFORMATION")}</ResourceTitle>
          <CpuInfoGrid>
            {Object.entries(cpuInfo)
              .filter(([key]) => key !== 'topology')
              .map(([key, value]) => (
                <CpuInfoItem key={key}>
                  <CpuInfoLabel>{key}:</CpuInfoLabel>
                  <CpuInfoValue>{String(value)}</CpuInfoValue>
                </CpuInfoItem>
              ))}
          </CpuInfoGrid>

          {cpuInfo?.topology && Object.keys(cpuInfo.topology).length > 0 && (
            <>
              <ResourceTitle style={{ marginTop: '20px' }}>{t("CPU_TOPOLOGY")}</ResourceTitle>
              <CpuInfoGrid>
                {Object.entries(cpuInfo.topology).map(([key, value]) => (
                  <CpuInfoItem key={key}>
                    <CpuInfoLabel>{key}:</CpuInfoLabel>
                    <CpuInfoValue>{String(value)}</CpuInfoValue>
                  </CpuInfoItem>
                ))}
              </CpuInfoGrid>
            </>
          )}
        </CpuInfoSection>
      )}
    </Container>
  )
}

export default Overview
